import React, { useMemo } from 'react';
import { Plus, Users as UsersIcon, Trash2, Pencil, Users } from 'lucide-react';
import {
  Button,
  Badge,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  DataTable,
} from 'lift-ui';
import type { TableColumn } from 'lift-ui';
import { User, UserGroup } from '../types';
import { getGroupUserCount, formatProductName } from '../utils';
import '../iam-support.styles.scss';

interface GroupManagementProps {
  groups: UserGroup[];
  users?: User[];
  loading?: boolean;
  searchTerm?: string;
  onCreateGroup: () => void;
  onAssignPermission: (group: UserGroup) => void;
  onEditMembers: (group: UserGroup) => void;
  onDeleteGroup: (groupId: number) => void;
  pagination?: {
    currentPage: number;
    totalPages: number;
    pageSize: number;
    totalItems: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    pageSizeOptions?: number[];
  };
}

const GroupManagement: React.FC<GroupManagementProps> = ({
  groups,
  users = [],
  loading = false,
  searchTerm = '',
  onCreateGroup,
  onAssignPermission,
  onEditMembers,
  onDeleteGroup,
  pagination,
}) => {
  const filteredGroups = groups.filter(
    (group) =>
      group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      group.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPermissionBadgeVariant = (level: string): 'default' | 'secondary' | 'outline' => {
    switch (level.toLowerCase()) {
      case 'manager':
        return 'default';
      case 'editor':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const dataTableColumns = useMemo((): TableColumn<UserGroup>[] => [
    {
      key: 'name',
      header: 'Group Name',
      accessor: 'name',
      render: (_v, row) => <span className="iam-support-name-cell font-medium">{row.name}</span>,
    },
    {
      key: 'description',
      header: 'Description',
      accessor: (row) => row.description ?? '',
      render: (_v, row) => (
        <span className="text-gray-600 max-w-xs truncate block">
          {row.description || <span className="text-gray-400">No description</span>}
        </span>
      ),
    },
    {
      key: 'members',
      header: 'Members',
      accessor: (row) => row.members?.length || getGroupUserCount(row),
      render: (_v, row) => {
        const memberCount = row.members?.length || getGroupUserCount(row);
        return (
          <div className="iam-support-group-members flex items-center gap-2">
            <Badge variant="outline">
              {memberCount} user{memberCount !== 1 ? 's' : ''}
            </Badge>
          </div>
        );
      },
    },
    {
      key: 'product_permissions',
      header: 'Product Permissions',
      accessor: (row) => Object.keys(row.permissions).length,
      render: (_v, row) => {
        const permissionCount = Object.keys(row.permissions).length;
        return (
          <div className="iam-support-permissions">
            {permissionCount > 0 ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <div className="flex flex-wrap gap-1 cursor-help">
                      {Object.entries(row.permissions).slice(0, 2).map(([product, level]) => (
                        <Badge
                          key={product}
                          variant={getPermissionBadgeVariant(level)}
                          className="text-xs"
                        >
                          {formatProductName(product)}: {level}
                        </Badge>
                      ))}
                      {permissionCount > 2 && (
                        <Badge variant="outline" className="text-xs">
                          +{permissionCount - 2} more
                        </Badge>
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="text-sm">
                      {Object.entries(row.permissions).map(([product, level]) => (
                        <div key={product} className="py-1">
                          {formatProductName(product)}: <strong>{level}</strong>
                        </div>
                      ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <span className="text-gray-400 text-sm">No permissions</span>
            )}
          </div>
        );
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      accessor: (row) => row.id,
      width: '180px',
      align: 'center',
      render: (_v, row) => (
        <div className="iam-support-actions flex gap-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => onAssignPermission(row)}>
                  <Plus className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Assign Permission</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => onEditMembers(row)}>
                  <UsersIcon className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Edit Members</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                  onClick={() => onDeleteGroup(row.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Delete Group</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ),
    },
  ], [onAssignPermission, onEditMembers, onDeleteGroup]);

  return (
    <>
      <div className="iam-support-table-card border-0 shadow-none rounded-xl overflow-hidden bg-white flex flex-col flex-1 min-h-0">
        <div className="flex justify-between items-center p-4 bg-white rounded-t-xl">
          <div className="flex-1 flex justify-start items-center gap-2">
            <Users className="iam-support-icon" />
            <div className="text-base font-semibold leading-[19.2px] text-[#242424]">
              <div>
                <h3 className="text-base font-semibold leading-[19.2px] tracking-tight">User Groups</h3>
                <p className="text-sm text-muted-foreground">
                  Manage user groups and assign permissions to groups. Group permissions are inherited by all members.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="iam-support-table-wrapper flex flex-col flex-1 min-h-0 p-4" style={{ width: '100%' }}>
          <DataTable<UserGroup>
            className="flex-1 min-h-0"
            maxHeight="100%"
            columns={dataTableColumns}
            data={filteredGroups}
            loading={loading}
            emptyMessage={
              searchTerm
                ? 'No groups found matching your search'
                : 'No groups available. Create one to get started.'
            }
            stickyHeader
            stickyFooter
            pagination={pagination}
            showActions={false}
          />
        </div>
      </div>
    </>
  );
};

export default GroupManagement;
